import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Phone, Mail, MapPin, Scale, Facebook, Twitter, Instagram, Linkedin, MessageCircle } from 'lucide-react';
import { officeInfo } from '../data/mock';

const Footer = () => {
  const { t, language } = useLanguage();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const quickLinks = [
    { key: 'home', section: 'hero' },
    { key: 'about', section: 'about' },
    { key: 'services', section: 'services' },
    { key: 'team', section: 'team' },
    { key: 'testimonials', section: 'testimonials' },
    { key: 'contact', section: 'contact' }
  ];

  const socialLinks = [
    { icon: Facebook, url: '#', label: 'Facebook' },
    { icon: Twitter, url: '#', label: 'Twitter' },
    { icon: Instagram, url: '#', label: 'Instagram' },
    { icon: Linkedin, url: '#', label: 'LinkedIn' }
  ];

  return (
    <footer className="relative bg-slate-50 border-t-2 border-amber-200">
      <div className="container mx-auto px-4">
        {/* Main Footer Content */}
        <div className="py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* About Section */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center shadow-lg">
                <Scale className="w-7 h-7 text-white" />
              </div>
              <div>
                <div className="text-lg font-bold text-slate-900">
                  {language === 'ar' ? 'مكتب فاطمة عبدالقدوس العبيدلي' : 'Fatima Abdulqudous Al Obeidli'}
                </div>
                <div className="text-xs text-amber-600">
                  {language === 'ar' ? 'للمحاماة' : 'Law Office'}
                </div>
              </div>
            </div>
            <p className="text-slate-600 leading-relaxed text-sm">
              {t('footerAboutText')}
            </p>
            
            {/* Social Links */}
            <div className="flex gap-3">
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-amber-100 border border-amber-300 flex items-center justify-center text-amber-600 hover:bg-amber-200 hover:border-amber-400 hover:scale-110 transition-all duration-300"
                    aria-label={social.label}
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-slate-900">
              {t('footerQuickLinks')}
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.section)}
                    className="text-slate-600 hover:text-amber-600 transition-colors duration-300 text-sm flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full group-hover:bg-amber-600 transition-colors" />
                    {t(link.key)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-slate-900">
              {t('services')}
            </h3>
            <ul className="space-y-3">
              {[1, 2, 3, 4, 5, 6].map((num) => (
                <li key={num}>
                  <button
                    onClick={() => scrollToSection('services')}
                    className="text-slate-600 hover:text-amber-600 transition-colors duration-300 text-sm flex items-center gap-2 group text-left"
                  >
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full group-hover:bg-amber-600 transition-colors" />
                    {t(`service${num}Title`)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-slate-900">
              {t('footerContact')}
            </h3>
            <ul className="space-y-4">
              <li>
                <a 
                  href={`tel:${officeInfo.phone}`}
                  className="flex items-start gap-3 text-slate-600 hover:text-amber-600 transition-colors duration-300 group"
                >
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center group-hover:bg-amber-200 transition-colors flex-shrink-0 mt-0.5">
                    <Phone className="w-4 h-4" />
                  </div>
                  <div className="text-sm">
                    <div className="font-medium mb-1">{language === 'ar' ? 'الهاتف' : 'Phone'}</div>
                    <div className="text-slate-500" dir="ltr">{officeInfo.phone}</div>
                  </div>
                </a>
              </li>
              <li>
                <a 
                  href="https://wa.me/971502060230"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-slate-600 hover:text-green-600 transition-colors duration-300 group"
                >
                  <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors flex-shrink-0 mt-0.5">
                    <MessageCircle className="w-4 h-4 text-green-600" />
                  </div>
                  <div className="text-sm">
                    <div className="font-medium mb-1">{language === 'ar' ? 'واتساب' : 'WhatsApp'}</div>
                    <div className="text-slate-500" dir="ltr">+971 50 206 0230</div>
                  </div>
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${officeInfo.email}`}
                  className="flex items-start gap-3 text-slate-600 hover:text-amber-600 transition-colors duration-300 group"
                >
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center group-hover:bg-amber-200 transition-colors flex-shrink-0 mt-0.5">
                    <Mail className="w-4 h-4" />
                  </div>
                  <div className="text-sm">
                    <div className="font-medium mb-1">{language === 'ar' ? 'البريد' : 'Email'}</div>
                    <div className="text-slate-500" dir="ltr">{officeInfo.email}</div>
                  </div>
                </a>
              </li>
              <li>
                <a 
                  href={officeInfo.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-slate-600 hover:text-amber-600 transition-colors duration-300 group"
                >
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center group-hover:bg-amber-200 transition-colors flex-shrink-0 mt-0.5">
                    <MapPin className="w-4 h-4" />
                  </div>
                  <div className="text-sm">
                    <div className="font-medium mb-1">{language === 'ar' ? 'العنوان' : 'Location'}</div>
                    <div className="text-slate-500 leading-relaxed">
                      {language === 'ar' 
                        ? 'كورنيش بلازا 1، الطابق 3، الشارقة'
                        : 'Corniche Plaza 1, 3rd Floor, Sharjah'}
                    </div>
                  </div>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t-2 border-amber-200 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-600 text-sm text-center md:text-left">
              © {new Date().getFullYear()} {officeInfo.name[language]}. {t('footerRights')}.
            </p>
            <div className="flex gap-6 text-sm">
              <button className="text-slate-600 hover:text-amber-600 transition-colors">
                {language === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}
              </button>
              <button className="text-slate-600 hover:text-amber-600 transition-colors">
                {language === 'ar' ? 'الشروط والأحكام' : 'Terms & Conditions'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative gradient */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500 to-transparent" />
    </footer>
  );
};

export default Footer;
